import { useState, useCallback, useRef } from "react";
import { ConfirmModal } from "@/shared/ui";

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "danger" | "warning" | "info";
  onConfirm?: () => void | Promise<void>;
}

export const useConfirm = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      // Usar setTimeout para asegurar que el estado se actualice después del evento actual
      setTimeout(() => {
        setOptions({
          ...opts,
          onConfirm: async () => {
            setIsLoading(true);
            try {
              if (opts.onConfirm) {
                await opts.onConfirm();
              }
              setIsOpen(false);
              setOptions(null);
              if (resolveRef.current) {
                resolveRef.current(true);
                resolveRef.current = null;
              }
            } catch (error) {
              console.error("Error in confirm action:", error);
              // Don't close modal on error, let user retry
            } finally {
              setIsLoading(false);
            }
          },
        });
        setIsOpen(true);
      }, 0);
    });
  }, []);

  const handleClose = useCallback(() => {
    if (!isLoading && resolveRef.current) {
      setIsOpen(false);
      resolveRef.current(false);
      setOptions(null);
      resolveRef.current = null;
    }
  }, [isLoading]);

  const Modal = options ? (
    <ConfirmModal
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={options.onConfirm || (() => {})}
      title={options.title}
      message={options.message}
      confirmText={options.confirmText}
      cancelText={options.cancelText}
      variant={options.variant}
      isLoading={isLoading}
    />
  ) : null;

  return { confirm, Modal };
};
